/* =============================================================================
   O conjunto de dados de exemplo, em português do Brasil
   -----------------------------------------------------------------------------
   The Portuguese half of the sample dataset. It is NOT part of `pt-BR.ts`, and
   that separation is the point: `pt-BR.ts` holds the CHROME — the words the
   interface says about itself — and is typed against `en.ts` so a missing key
   cannot compile. These are the words the interface says about its DATA, keyed
   by each record's stable identifier, and their English is not in a catalogue at
   all: it is the fixture record itself, which is what keeps the English output
   byte-identical and stops a translation of a demo sentence from ever being the
   copy of record.

   Keyed by id, never by position and never by the English words: see the header
   of `fixtures.ts` for why, and `fixtures.ts`'s `enumKey` for the one place a
   value is the identifier.

   Written as a Brazilian construction and finance professional writes: obra and
   empreendimento, aportes, medição de avanço, prestação de contas, alvará,
   habite-se, vistoria. Proper nouns — people, companies, streets, the
   developments' own names — are untouched, because a name is the thing itself
   and does not translate.
   ============================================================================= */

export const FIXTURE_PT_BR: Readonly<Record<string, string>> = {
  /* ---- A obra: etapas ------------------------------------------------------
     The nine phases of the document's §7, by the id every record carries. */
  'fixture.phase.planning': 'Planejamento e pré-obra',
  'fixture.phase.acquisition': 'Aquisição e mobilização',
  'fixture.phase.demolition': 'Demolição e preparação do terreno',
  'fixture.phase.foundation': 'Fundação',
  'fixture.phase.framing': 'Estrutura e telhado',
  'fixture.phase.utilities': 'Instalações e vedação',
  'fixture.phase.finishes': 'Acabamentos',
  'fixture.phase.landscaping': 'Paisagismo',
  'fixture.phase.completion': 'Conclusão e venda',

  /* ---- O cadastro: especialidades e situação ------------------------------- */
  'fixture.trade.sitework': 'Terraplenagem',
  'fixture.trade.concrete': 'Concreto',
  'fixture.trade.framing': 'Estrutura',
  'fixture.trade.roofing': 'Telhados',
  'fixture.trade.electrical': 'Elétrica',
  'fixture.trade.plumbing': 'Hidráulica',
  'fixture.trade.hvac': 'Climatização',
  'fixture.trade.drywall': 'Drywall',
  'fixture.trade.cabinetry': 'Marcenaria',
  'fixture.trade.flooring': 'Pisos',
  'fixture.trade.painting': 'Pintura',
  'fixture.trade.landscaping': 'Paisagismo',
  'fixture.trade.windows': 'Esquadrias',
  'fixture.trade.insulation': 'Isolamento',
  'fixture.trade.masonry': 'Alvenaria',

  'fixture.contractorStatus.awarded': 'Contratado',
  'fixture.contractorStatus.in-progress': 'Em andamento',
  'fixture.contractorStatus.on-hold': 'Paralisado',
  'fixture.contractorStatus.closed': 'Encerrado',
  'fixture.contractorStatus.disputed': 'Em disputa',

  /* ---- O razão: categoria e a fatia da etapa ------------------------------- */
  'fixture.expenseCategory.materials': 'Materiais',
  'fixture.expenseCategory.labour': 'Mão de obra',
  'fixture.expenseCategory.equipment': 'Equipamentos',
  'fixture.expenseCategory.permits-and-fees': 'Alvarás e taxas',
  'fixture.expenseCategory.professional-services': 'Serviços profissionais',
  'fixture.expenseCategory.land-and-acquisition': 'Terreno e aquisição',
  'fixture.expenseCategory.contingency': 'Contingência',

  'fixture.expenseDraw.initial': 'liberação inicial',
  'fixture.expenseDraw.progress': 'medição de avanço',
  'fixture.expenseDraw.materials': 'liberação de materiais',
  'fixture.expenseDraw.retention': 'liberação de retenção',
  'fixture.expenseDraw.final': 'acerto final',

  /* ---- O empreendimento: a etapa em que está ------------------------------- */
  'fixture.project.lakeview.phase': 'Acabamentos',
  'fixture.project.maple-court.phase': 'Estrutura e telhado',
  'fixture.project.birch-hollow.phase': 'Fundação',

  /* ---- A equipe: função --------------------------------------------------- */
  'fixture.personRole.investor': 'Investidor',
  'fixture.personRole.finance': 'Financeiro',
  'fixture.personRole.site-lead': 'Encarregado de obra',
  'fixture.personRole.coordinator': 'Coordenador',
  'fixture.personRole.approver': 'Aprovador',
  'fixture.personRole.field': 'Campo',
  'fixture.personRole.back-office': 'Retaguarda',

  /* ---- Marcos ------------------------------------------------------------- */
  'fixture.milestone.m-lv-1': 'Estrutura concluída',
  'fixture.milestone.m-lv-2': 'Telhado concluído e teste de estanqueidade aprovado',
  'fixture.milestone.m-lv-3': 'Vistoria municipal aprovada, sem apontamentos',
  'fixture.milestone.m-lv-4': 'Instalações embutidas concluídas nos dois pavimentos',
  'fixture.milestone.m-lv-5': 'Vedação concluída e estanque',
  'fixture.milestone.m-lv-6': 'Início da instalação dos armários',
  'fixture.milestone.m-lv-7': 'Vistoria final e habite-se',
  'fixture.milestone.m-mc-1': 'Fundação concretada e curada',
  'fixture.milestone.m-mc-2': 'Estrutura do pavimento superior montada',
  'fixture.milestone.m-mc-3': 'Tesouras montadas e telhado fechado',
  'fixture.milestone.m-bh-1': 'Canteiro mobilizado e contenções de erosão instaladas',
  'fixture.milestone.m-bh-2': 'Estrutura existente demolida',

  /* ---- Evidências: o que cada foto mostra --------------------------------- */
  'fixture.evidence.plan-set-issued': 'Conjunto de projetos completo protocolado para alvará',
  'fixture.evidence.survey-stakes': 'Estacas do levantamento topográfico implantadas',
  'fixture.evidence.utility-locates': 'Marcações de localização das concessionárias',
  'fixture.evidence.lot-cleared-staked': 'Terreno limpo e estaqueado',
  'fixture.evidence.temp-power': 'Ligação provisória de energia executada',
  'fixture.evidence.initial-site-photos': 'Fotos iniciais do canteiro para registro',
  'fixture.evidence.structure-before-demolition': 'Estrutura existente antes da demolição',
  'fixture.evidence.demolition-in-progress': 'Demolição em andamento',
  'fixture.evidence.site-cleared-graded': 'Terreno limpo e nivelado',
  'fixture.evidence.excavation-to-grade': 'Escavação até a cota de projeto',
  'fixture.evidence.rebar-cage-inspected': 'Armação conferida antes da concretagem',
  'fixture.evidence.footings-poured': 'Sapatas concretadas',
  'fixture.evidence.slab-cured': 'Laje curada e liberada',
  'fixture.evidence.first-floor-framing': 'Estrutura do pavimento térreo',
  'fixture.evidence.second-floor-deck': 'Estrutura do pavimento superior',
  'fixture.evidence.trusses-delivered': 'Tesouras entregues',
  'fixture.evidence.roof-sheathing': 'Fechamento do telhado concluído',
  'fixture.evidence.roof-water-test': 'Teste de estanqueidade do telhado',
  'fixture.evidence.rough-in-electrical': 'Elétrica embutida',
  'fixture.evidence.rough-in-plumbing': 'Hidráulica embutida',
  'fixture.evidence.hvac-ductwork': 'Dutos de climatização',
  'fixture.evidence.enclosure-wrap': 'Vedação externa',
  'fixture.evidence.drywall-hung-taped': 'Drywall montado e tratado',
  'fixture.evidence.cabinet-install-started': 'Instalação dos armários iniciada',
  'fixture.evidence.paint-and-trim': 'Pintura e acabamento de madeira',
  'fixture.evidence.flooring-laid': 'Pisos assentados',
  'fixture.evidence.grading-and-drainage': 'Nivelamento e drenagem',
  'fixture.evidence.planting-plan-executed': 'Paisagismo executado',
  'fixture.evidence.driveway-poured': 'Calçada de acesso concretada',
  'fixture.evidence.final-walkthrough': 'Vistoria final de entrega',
  'fixture.evidence.punch-list-cleared': 'Lista de pendências concluída',
  'fixture.evidence.listing-preparation': 'Preparação para anúncio de venda',

  /* ---- Documentos --------------------------------------------------------- */
  'fixture.document.doc-1': 'Conjunto para alvará — desenhos de arquitetura',
  'fixture.document.doc-2': 'Orçamento base Rev. C',
  'fixture.document.doc-3': 'Contrato de compra do terreno',
  'fixture.document.doc-4': 'Certidão de matrícula do imóvel',
  'fixture.document.doc-5': 'Alvará de demolição',
  'fixture.document.doc-6': 'Nota fiscal do fornecedor de concreto 4471',
  'fixture.document.doc-7': 'Relatório de inspeção da armação',
  'fixture.document.doc-8': 'Memorial de cálculo das tesouras',
  'fixture.document.doc-9': 'Vistoria municipal da estrutura — aprovada',
  'fixture.document.doc-10': 'Orçamento da elétrica embutida — revisado',
  'fixture.document.doc-11': 'Nota fiscal dos equipamentos de climatização (entrega pendente)',
  'fixture.document.doc-12': 'Desenhos de marcenaria',
  'fixture.document.doc-13': 'Conjunto para alvará — desenhos de arquitetura',
  'fixture.document.doc-14': 'Contrato de compra do terreno',
  'fixture.document.doc-15': 'Vistoria da fundação — aprovada',
  'fixture.document.doc-16': 'Nota fiscal do pacote de madeira 8830',
  'fixture.document.doc-17': 'Estudo de viabilidade e pro forma',
  'fixture.document.doc-18': 'Contrato de compra — 41 Birch Hollow',
  'fixture.document.doc-19': 'Plano de controle de erosão',
  'fixture.document.doc-20': 'Detalhamento do custo de mobilização',

  /* ---- Publicações: título, período e o texto ----------------------------- */
  'fixture.publication.pub-lv-09.title': 'Estrutura concluída e vistoria aprovada',
  'fixture.publication.pub-lv-09.period': 'Setembro de 2026',
  'fixture.publication.pub-lv-09.body':
    'Estrutura e telhado encerrados no prazo. A vistoria municipal foi aprovada sem apontamentos, e a etapa fechou US$ 28.400 abaixo do valor publicado em junho.',
  'fixture.publication.pub-lv-08.title': 'Elétrica e hidráulica iniciadas em paralelo',
  'fixture.publication.pub-lv-08.period': 'Início de setembro de 2026',
  'fixture.publication.pub-lv-08.body':
    'Duas frentes de serviço abertas junto com os acabamentos do pavimento superior. Os materiais foram entregues e conferidos contra a nota fiscal antes de qualquer lançamento.',
  'fixture.publication.pub-lv-10.title': 'Instalações embutidas concluídas e vedação programada',
  'fixture.publication.pub-lv-10.period': 'Outubro de 2026',
  'fixture.publication.pub-lv-10.body':
    'Rascunho — aguardando a conferência financeira da nota fiscal da climatização antes de mostrar aos investidores. O equipamento foi faturado, mas ainda não foi entregue.',
  'fixture.publication.pub-lv-11.title': 'Pacote de marcenaria liberado para a fábrica',
  'fixture.publication.pub-lv-11.period': 'Outubro de 2026',
  'fixture.publication.pub-lv-11.body':
    'Rascunho — desenhos de marcenaria anexados, aguardando a confirmação do prazo de entrega antes de escrever o texto.',
  'fixture.publication.pub-mc-06.title': 'Tesouras entregues e telhado fechado',
  'fixture.publication.pub-mc-06.period': 'Outubro de 2026',
  'fixture.publication.pub-mc-06.body':
    'Aprovado e pronto para publicar. O telhado está fechado e a etapa de vedação pode começar antes do previsto.',
  'fixture.publication.pub-mc-05.title': 'Fundação concretada e curada',
  'fixture.publication.pub-mc-05.period': 'Agosto de 2026',
  'fixture.publication.pub-mc-05.body':
    'A fundação passou na vistoria de primeira e curou dentro da janela prevista.',
  'fixture.publication.pub-mc-07.title': 'Avanço da estrutura e laje do pavimento superior',
  'fixture.publication.pub-mc-07.period': 'Setembro de 2026',
  'fixture.publication.pub-mc-07.body':
    'A estrutura do pavimento superior está montada e a obra segue no prazo. Duas frentes abertas ao mesmo tempo.',
  'fixture.publication.pub-bh-02.title': 'Canteiro mobilizado e controle de erosão instalado',
  'fixture.publication.pub-bh-02.period': 'Setembro de 2026',
  'fixture.publication.pub-bh-02.body':
    'O canteiro está mobilizado e o controle de erosão foi instalado antes da etapa de demolição.',
  'fixture.publication.pub-bh-03.title': 'Demolição programada',
  'fixture.publication.pub-bh-03.period': 'Novembro de 2026',
  'fixture.publication.pub-bh-03.body':
    'Rascunho — aguardando a confirmação da empreiteira de demolição antes de publicar a data.',

  /* ---- Relatórios --------------------------------------------------------- */
  'fixture.report.rep-1.title': 'Lakeview Residence — Setembro de 2026',
  'fixture.report.rep-1.period': 'Setembro de 2026',
  'fixture.report.rep-1.scope': 'Período',
  'fixture.report.rep-2.title': 'Lakeview Residence — Estrutura e telhado',
  'fixture.report.rep-2.period': 'Mar – Set 2026',
  'fixture.report.rep-2.scope': 'Etapa',
  'fixture.report.rep-3.title': 'Lakeview Residence — Agosto de 2026',
  'fixture.report.rep-3.period': 'Agosto de 2026',
  'fixture.report.rep-3.scope': 'Período',
  'fixture.report.rep-4.title': 'Maple Court Residence — Setembro de 2026',
  'fixture.report.rep-4.period': 'Setembro de 2026',
  'fixture.report.rep-4.scope': 'Período',
  'fixture.report.rep-5.title': 'Maple Court Residence — Fundação',
  'fixture.report.rep-5.period': 'Mai – Ago 2026',
  'fixture.report.rep-5.scope': 'Etapa',
  'fixture.report.rep-6.title': 'Birch Hollow Residence — Setembro de 2026',
  'fixture.report.rep-6.period': 'Setembro de 2026',
  'fixture.report.rep-6.scope': 'Período',
  'fixture.report.rep-7.title': 'Lakeview Residence — relatório final',
  'fixture.report.rep-7.period': 'Obra completa',
  'fixture.report.rep-7.scope': 'Obra completa',

  /* ---- Pipeline: a nota de cada oportunidade ------------------------------ */
  'fixture.opportunity.op-1':
    'Terreno sob opção até 15 de novembro. Sondagem do solo contratada.',
  'fixture.opportunity.op-2':
    'Duas opções de implantação orçadas. Aguardando o cálculo de drenagem do engenheiro civil.',
  'fixture.opportunity.op-3':
    'Vantage Ridge Partners analisando o material. Marlow Capital demonstrou interesse.',
  'fixture.opportunity.op-4':
    'Totalmente integralizado. Assinatura agendada; documentação do imóvel concluída.',
  'fixture.opportunity.op-5':
    'Alvará emitido. Em mobilização; ainda não migrado para a plataforma como obra ativa.',
  'fixture.opportunity.op-6': 'Zoneamento para duplex confirmado. Pro forma elaborada.',

  /* ---- Notificações ------------------------------------------------------- */
  'fixture.notification.n-1.title': 'Etapa de estrutura fechada abaixo do orçamento',
  'fixture.notification.n-1.description':
    'Lakeview fechou US$ 28.400 abaixo do valor publicado. A atualização já está no portal do investidor.',
  'fixture.notification.n-2.title': 'Vistoria agendada para 02 de outubro',
  'fixture.notification.n-2.description':
    'A vistoria de instalação das esquadrias está agendada. Dois documentos ainda aguardam aprovação antes da publicação.',
  'fixture.notification.n-3.title': 'Três despesas precisam de conferência',
  'fixture.notification.n-3.description':
    'Maple Court tem três despesas lançadas sem nota fiscal correspondente.',
  'fixture.notification.n-4.title': 'Contrato de isolamento vencido',
  'fixture.notification.n-4.description':
    'O contrato de isolamento de Birch Hollow venceu em 12 de setembro e precisa ser reemitido.',
  'fixture.notification.n-5.title': 'Elétrica e hidráulica iniciadas',
  'fixture.notification.n-5.description':
    'Duas frentes de serviço abertas em paralelo. Oito fotos foram anexadas à etapa.',
  'fixture.notification.n-6.title': 'Novo investidor incluído em Maple Court',
  'fixture.notification.n-6.description':
    'Uma participação de 9% foi registrada e o contrato de participação arquivado.',

  /* ---- Atualizações publicadas -------------------------------------------- */
  'fixture.update.u-2026-09-18.title': 'Estrutura concluída e vistoria aprovada',
  'fixture.update.u-2026-09-18.body':
    'Estrutura e telhado encerrados no prazo. A vistoria municipal foi aprovada sem apontamentos, e a etapa fechou US$ 28.400 abaixo do valor publicado em junho.',
  'fixture.update.u-2026-09-04.title': 'Elétrica e hidráulica iniciadas em paralelo',
  'fixture.update.u-2026-09-04.body':
    'Duas frentes de serviço abertas junto com os acabamentos do pavimento superior. Os materiais foram entregues e conferidos contra a nota fiscal antes de qualquer lançamento.',
  'fixture.update.u-2026-08-21.title': 'Telhado concluído e teste de estanqueidade aprovado',
  'fixture.update.u-2026-08-21.body':
    'O telhado foi concluído nove dias antes do previsto em junho. O teste de estanqueidade passou de primeira.',
  'fixture.update.u-2026-08-07.title': 'Pedido das esquadrias confirmado para o início de outubro',
  'fixture.update.u-2026-08-07.body':
    'O pacote de esquadrias foi liberado para o fornecedor com janela de entrega confirmada. A instalação está programada para a primeira semana de outubro.',
  'fixture.update.u-2026-07-24.title': 'Estrutura concluída',
  'fixture.update.u-2026-07-24.body':
    'A última tesoura foi montada e a estrutura está completa. As instalações embutidas começam na próxima semana.',
};
